import { Badge, cn } from "@gotts.ai/ui";
import { ChevronsUpDown, Eye, Pencil } from "lucide-react";
import { useState } from "react";

import { PcbCard } from "../../components/PcbCard";

interface FunctionSectionProps {
  type: "read" | "write";
  count: number;
  children: (collapsed: boolean) => React.ReactNode;
}

export function FunctionSection({
  type,
  count,
  children,
}: FunctionSectionProps) {
  const [allCollapsed, setAllCollapsed] = useState(false);
  const isRead = type === "read";

  return (
    <PcbCard
      label={isRead ? "READ" : "WRITE"}
      cut={10}
      className={cn(
        isRead
          ? "[&:hover_.pcb-border_.edge]:stroke-[rgba(52,209,106,0.45)] [&_.pcb-border_.edge]:stroke-[rgba(52,209,106,0.2)]"
          : "[&:hover_.pcb-border_.edge]:stroke-[rgba(245,158,11,0.45)] [&_.pcb-border_.edge]:stroke-[rgba(245,158,11,0.2)]",
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 pb-2">
        {isRead ? (
          <Eye size={14} className="shrink-0 text-green-400" />
        ) : (
          <Pencil size={14} className="shrink-0 text-amber-400" />
        )}
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text)]">
          {isRead ? "Read" : "Write"} Functions
        </span>
        <Badge
          className={cn(
            "text-[9px]",
            isRead
              ? "bg-green-500/20 text-green-400"
              : "bg-amber-500/20 text-amber-400",
          )}
        >
          {count}
        </Badge>
        <button
          type="button"
          onClick={() => {
            setAllCollapsed(!allCollapsed);
          }}
          className="ml-auto flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]"
          title={allCollapsed ? "Expand all" : "Collapse all"}
        >
          <ChevronsUpDown size={12} />
          {allCollapsed ? "Expand" : "Collapse"}
        </button>
      </div>

      {/* Divider */}
      <div
        className={cn(
          "-mx-5 h-px",
          isRead ? "bg-green-500/10" : "bg-amber-500/10",
        )}
      />

      {/* Children — functions list */}
      <div className="-mx-5 -mb-5">{children(allCollapsed)}</div>
    </PcbCard>
  );
}
